import {
  clone,
  cloneDeep,
  eachRight,
  filter,
  find,
  findIndex,
  first,
  get,
  has,
  isArray,
  isEmpty,
  isEqual,
  isString,
  map,
  merge,
  omit,
  set,
  some,
  unionBy,
} from "lodash";
import {
  CHAT_APP_DEFAULTS,
  PREVIEW_APP_Fields,
  PREVIEW_APP_ID_PATH,
  PREVIEW_APP_METADATA_V2_PATH,
  PREVIEW_CHATAI_PUBLISH_INPUT_TYPES,
  PREVIEW_CHATAI_PUBLISH_OUTPUT_TYPES,
  PREVIEW_MERGE_UPLOAD_EXCLUDE_PATHS,
  PreviewAppTypeMap,
  getDefaultPreviewAppTypeList,
} from "./const";
import { WEB_APP_DEFAULT_SUBTITLE, WEB_APP_DEFAULT_TITLE } from "@views/thinking-layout-editor/constants";
import { HomeUsePluginUtil } from "@uikit/util/HomeUsePluginUtil";
import { DisplayType } from "../Nodes/typing";
import { Node, Edge } from "reactflow";
import store from "@store/index";
import func from "@uikit/func";
import { AYLWAYS_SHOW_OUTPUT, WITHOUT_INPUT_ID } from "../AISaas/AddToContent/conts";
import { ReactFlowNodeUtil } from "@uikit/util/ReactFlowNodeUtil";
import { isOnlyChatInterface } from "@views/thinking-layout-editor/util";
import { uploadTypes } from "../Nodes/constants";
import { getIt } from "@uikit/getIt";
import { ChatAIStore } from "../ChatAI/ChatAIStore";
import { HomePluginStore } from "imagica-corekit/dist/base/store/HomePluginStore";
import { StoryNodeDisplayType } from "imagica-corekit/dist/base/storyV2/domain/StoryNodeDisplayType";

const APP_NAME_TEMPLATE = "My {type} {count}";

const appTypeList = getDefaultPreviewAppTypeList();
const aiApp = appTypeList.find(item => item.appType === PreviewAppTypeMap.AI_APP);
const staticApp = appTypeList.find(item => item.appType === PreviewAppTypeMap.STATIC_WEB);

export class PreviewAppUtil {
  static genDefaultUISaasValue(): PreviewApp.UISaasValueType {
    return {
      id: "",
      title: WEB_APP_DEFAULT_TITLE,
      showTitle: true,
      titleStyle: {},
      titleV3: {},
      subTitle: WEB_APP_DEFAULT_SUBTITLE,
      showSubTitle: true,
      subTitleStyle: {},
      subTitleV3: {},
      placeholder: "Enter something",
      theme: "light",
      fontSize: 12,
      staticMode: false,
      input: [] as any[],
      output: [] as AISaasOutput.ThoughtType[],
      fromCotTitle: "",
      fromCotSubtitle: "",
      settings: {
        branding: true,
      },
      v2App: {
        id: "",
        type: undefined,
        appDescription: "",
        appName: "",
      },
    };
  }

  /**
   * 判断是否是默认的 app value
   *
   * @param app
   * @returns
   */
  static isDefaultPreviewApp(app?: PreviewApp.UISaasValueType): boolean {
    return app ? isEqual(PreviewAppUtil.genDefaultUISaasValue(), app) : true;
  }

  static removeByNodeIds(value: PreviewApp.UISaasValueType[], ids: string[]): PreviewApp.UISaasValueType[] {
    return value.map(app => {
      return {
        ...app,
        input: app.input.filter(item => !ids.includes(item.id)),
        output: app.output.filter(item => !ids.includes(item.results[0].nodeId)),
      };
    });
  }
  static assignAppId(target: any, appId?: PreviewApp.UISaasValueType["id"]) {
    return appId ? set(target, PREVIEW_APP_ID_PATH, appId) : target;
  }

  static assignAppList(target: any, extra: PreviewApp.ExtraArgsType) {
    const appList = get(extra, "appList", []);
    const newAppList = extra.nodes ? PreviewAppUtil.mergeNodesWithAppList(appList, extra.nodes) : appList;
    // 应该不在需要使用 v2
    if (!extra.isNewEdge) {
      return set(target, ["v2", "UI"], newAppList);
    }
    return set(target, ["v3", "UI"], newAppList);
  }

  static assignPublishMeta(target: any, extra: PreviewApp.ExtraArgsType) {
    const { appId, publishedMetadata } = extra;

    if (!appId || !publishedMetadata) {
      return target;
    }

    // 扩展 publish_meta data
    // 扩展一份出来,防止无法修改
    const metadataV2List = get(target, PREVIEW_APP_METADATA_V2_PATH) as
      | PreviewApp.PublishedMetadataV2Type[]
      | undefined;
    const metadataList = isArray(metadataV2List) ? clone(metadataV2List) : [];
    const targetIndex = findIndex(metadataList, [PREVIEW_APP_Fields.DATA_APP_ID, appId]);

    // 考虑到可能会有 v2 在 publishedMetadata 中
    const _publishedMetadata = omit(publishedMetadata, PREVIEW_APP_Fields.PREVIEW_METADATA_V2);

    // 注入 appid
    set(_publishedMetadata, PREVIEW_APP_Fields.DATA_APP_ID, appId);

    if (targetIndex < 0) {
      metadataList.push(_publishedMetadata);
    } else {
      const newPublishedMetadata = omit(_publishedMetadata, [
        "stripe_account_id",
        "stripe_price_id",
        "stripe_product_id",
      ]);
      // 因为preview v2 版本之前的数据是直接通过publishMetadata的数据更新的， v2是通过PublishedMetadataV2 更新的
      metadataList[targetIndex] = {
        ...metadataList[targetIndex],
        ...newPublishedMetadata,
      };
    }
    return set(target, PREVIEW_APP_METADATA_V2_PATH, metadataList);
  }

  static assignChatAIType(target: any, nodes: Node[]) {
    const UIList = get(target, ["v3", "UI"]);
    if (!isArray(UIList)) {
      return target;
    }

    const newUIList = UIList.map((item: PreviewApp.UISaasValueType) => {
      if (!PreviewAppUtil.isChatAiApp(item)) {
        return item;
      }
      const chatMode = PreviewAppUtil.getChatAiMode({ saasUIData: item, nodes });
      return {
        ...item,
        v2App: {
          ...item.v2App,
          chatMode,
        },
      };
    });

    return set(target, ["v3", "UI"], newUIList);
  }

  static assignLastIndex(target: any, lastIndex: number) {
    return Object.assign({}, target, { lastIndex });
  }

  static assignPreviewAppData(target: any, extra: PreviewApp.ExtraArgsType) {
    if (isEmpty(extra)) {
      return target;
    }

    // 防止项目数据被污染 深拷贝一份
    target = cloneDeep(target);

    if (extra.appId) {
      target = PreviewAppUtil.assignAppId(target, extra.appId);
    }

    if (extra.appList) {
      target = PreviewAppUtil.assignAppList(target, extra);
    }

    if (extra.publishedMetadata) {
      target = PreviewAppUtil.assignPublishMeta(target, extra);
    }

    if (extra.nodes) {
      target = PreviewAppUtil.assignChatAIType(target, extra.nodes);
    }

    if (extra.lastIndex) {
      target = PreviewAppUtil.assignLastIndex(target, extra.lastIndex);
    }

    return target;
  }

  /**
   * 获取目标对象里的 publish_metadata_v2
   * @param target
   * @returns
   */
  static getPublishMeta(target: any) {
    return get(target, PREVIEW_APP_METADATA_V2_PATH, []) as PreviewApp.PublishedMetadataV2Type[];
  }

  static getTargetPublishMetaById(target: any, id: PreviewApp.PublishedMetadataV2Type["id"]) {
    const metalist = PreviewAppUtil.getPublishMeta(target);
    return find(metalist, ["id", id]);
  }

  static genAppId(index: number = 1): PreviewApp.UISaasValueType["id"] {
    return `appId-${index}`;
  }

  static getAppTypeCountByFormat(value: PreviewApp.UISaasValueType[], format: PreviewApp.FormatChooseAppType): number {
    return value.filter(item => item.v2App?.type === format.appType).length;
  }

  static genPreviewAppTitleByFormat(option: PreviewApp.FormatChooseAppType) {
    if (option.appType === PreviewAppTypeMap.CHAT_AI) {
      return {
        title: CHAT_APP_DEFAULTS.title,
        subTitle: CHAT_APP_DEFAULTS.subTitle,
      };
    }

    return {
      title: WEB_APP_DEFAULT_TITLE,
      subTitle: WEB_APP_DEFAULT_SUBTITLE,
    };
  }

  /**
   * 见 getUIListParam
   *
   * 并扩展 v2 版本预览初始数据
   *
   * @param saasUIData
   * @param extra
   * @returns
   */
  static genPreviewApp(
    saasUIData: PreviewApp.UISaasDataType,
    extra: Partial<PreviewApp.UISaasDataType> & {
      v2App: PreviewApp.PreviewAppV2FieldType;
    }
  ): PreviewApp.UISaasValueType {
    const { v2App, ...uidata } = extra;

    return {
      label: "",
      value: saasUIData.value || "saas",
      title: saasUIData.title,
      titleV3: saasUIData.titleV3 || {},
      subTitle: saasUIData.subTitle,
      subTitleV3: saasUIData.subTitleV3 || {},
      placeholder: saasUIData.placeholder,
      // TODO: 应该去掉 function
      input: saasUIData.input,
      // TODO: 应该去掉 function
      output: [...saasUIData.output],
      showTitle: saasUIData.showTitle,
      showSubTitle: saasUIData.showSubTitle,
      titleStyle: JSON.stringify(saasUIData.titleStyle),
      subTitleStyle: JSON.stringify(saasUIData.subTitleStyle),
      staticMode: saasUIData.staticMode,
      previewV2: saasUIData.previewV2,
      settings: saasUIData.settings,
      theme: saasUIData.theme,
      fontSize: saasUIData.fontSize,
      ...uidata,

      // v2 version
      id: v2App?.id,
      v2App: v2App,
    };
  }

  static getAppName(format: PreviewApp.FormatChooseAppType, count: number) {
    const { title } = format;
    if (!count || count < 1) {
      return APP_NAME_TEMPLATE.replace("{type}", title).replace(" {count}", "");
    }
    return APP_NAME_TEMPLATE.replace("{type}", title).replace("{count}", count.toString());
  }

  /**
   * FIXME: 生成的 appid 是从 1开始,并非0,所以如果外部传入的值小于等于1,则需要 +1
   * @param args
   * @param defaultV2App
   * @returns
   */
  static genV2App(
    args: Partial<PreviewApp.GenV2AppArgs>,
    defaultV2App?: Partial<PreviewApp.PreviewAppV2FieldType>
  ): PreviewApp.PreviewAppV2FieldType {
    const { lastIndex = 1, lastTypeIndex = 0, format } = args;
    const type = format?.appType;
    return {
      id: PreviewAppUtil.genAppId(lastIndex),
      type: type,
      appName: type ? PreviewAppUtil.getAppName(format, lastTypeIndex) : "",
      appDescription: "",
      ...defaultV2App,
    };
  }

  /**
   * 判断saasuidata是否是 static app
   * @param saasUiData
   * @returns
   */
  static isStaticApp(saasUiData: PreviewApp.UISaasValueType) {
    if (has(saasUiData, "v2App")) {
      return get(saasUiData, ["v2App", "type"]) === PreviewAppTypeMap.STATIC_WEB;
    }

    return saasUiData.staticMode;
  }

  static filterSaasValueFunctions(saasUIData: PreviewApp.UISaasValueType) {
    saasUIData.input = saasUIData.input.map(item => {
      return {
        ...item,
        data: HomeUsePluginUtil.getDeleteFunctionData(item.data),
      };
    });
    // saasUIData.output.forEach((item) => HomeUsePluginUtil.getDeleteFunctionData(item))

    return saasUIData;
  }

  /**
   *
   * @param project
   * @returns
   */
  static isV2PreviewProject(project: PreviewApp.ProjectType) {
    const publishv2 = get(project, PREVIEW_APP_METADATA_V2_PATH);
    return has(project, PREVIEW_APP_ID_PATH) && isArray(publishv2);
  }

  static isV2UIValue(uiItem: PreviewApp.UISaasValueType) {
    return has(uiItem, "id") && has(uiItem, "v2App");
  }

  /**
   * 合并一个新的 ui 列表, 并且为每一项数据增加 v2app 数据的数据,包含 id
   * @param projectUIList
   * @returns
   */
  static mergeNewUIList(projectUIList: PreviewApp.UISaasValueType[]) {
    return projectUIList.map((uiItem, index) => {
      if (PreviewAppUtil.isV2UIValue(uiItem)) {
        return uiItem;
      }
      const format = uiItem.staticMode ? staticApp : aiApp;
      const v2App = PreviewAppUtil.genV2App({ format, lastIndex: index + 1 });
      const id = get(uiItem, PREVIEW_APP_Fields.DATA_APP_ID, v2App?.id);
      return PreviewAppUtil.merge({ ...uiItem }, { id, v2App });
    });
  }

  static toV2PreviewProject(selectedExistingProject: PreviewApp.ProjectType) {
    // gen new data
    const UIData = get(selectedExistingProject, ["v3", "UI"]);
    // uilist
    const appList = UIData ? PreviewAppUtil.mergeNewUIList(UIData) : [];
    // appid
    const appId = first(appList)?.id || "";
    // publish meta
    const publish_meta = get(selectedExistingProject, "published_metadata") as any;
    const metadatalist = publish_meta ? [{ ...publish_meta, id: appId }] : [];

    // set new data
    // FIXME: 为了拦截修改 project, 深拷贝了一份 selectedExistingProject
    const result = cloneDeep(selectedExistingProject);
    set(result, PREVIEW_APP_METADATA_V2_PATH, metadatalist);
    // previewAppId
    set(result, PREVIEW_APP_ID_PATH, appId);
    // applist
    set(result, ["v3", "UI"], appList);

    return result;
  }

  static merge<T>(value: T, target: Partial<T>): T {
    // 保证返回一个新的 value
    return merge(cloneDeep(value), target);
  }

  /**
   * 合并 node 节点，可排除不合并的字段
   * @param target
   * @param source
   * @param excludePathList
   * @returns
   */
  static mergeNode<T extends Node>(target: T, source: T, excludePathList?: string[]): T {
    const newNode = PreviewAppUtil.merge(target, source);

    // 排除指定值
    if (isArray(excludePathList)) {
      excludePathList.forEach(path => {
        const targetValue = get(target, path);
        set(newNode, path, targetValue);
      });
    }

    return newNode;
  }

  static mergeValue<T extends PreviewApp.UISaasValueType>(value: T, target: Partial<T>): T {
    // 先合并一次
    const newValue = merge(value, target);

    // FIXME: 这是 saasuidat 到 appList 中最后一次保证 input 不重复的地方
    if (target.input) {
      // const targetIds = map(target.input, "id");
      // const newInput = newValue.input.filter(item => targetIds.includes(item.id));
      // newValue.input = newInput;
      // ??? 兜底方案，防止 input 重复，!!! 直接深拷贝一份input，并且过滤掉重复的 input
      newValue.input = unionBy(cloneDeep(target.input), "id");
    }

    if (target.output) {
      // const targetIds = target.output.map(item => {
      //   return item.results[0].previewId;
      // });
      // const newOutput = newValue.output.filter(item => {
      //   const nodeId = item.results[0].previewId;
      //   return targetIds.includes(nodeId);
      // });
      // newValue.output = newOutput;
      // ??? 兜底方案，防止o utput 重复，!!! 直接深拷贝一份output，并且过滤掉重复的 output
      newValue.output = PreviewAppUtil.unionOutput(cloneDeep(target.output));
    }

    return newValue;
  }

  static hasNodeIds(appList: PreviewApp.UISaasValueType[], ids: string[]) {
    for (const app of appList) {
      for (const input of app.input) {
        if (ids.includes(input.id)) {
          return true;
        }
      }

      for (const output of app.output) {
        for (const result of output.results) {
          if (ids.includes(result.nodeId)) {
            return true;
          }
        }
      }
    }

    return false;
  }

  /**
   * from `HomeUsePlugin` getContentByParam
   * @param type
   * @param value
   * @returns
   */
  static getContentByParam<T extends DisplayType>(type: T, value: PreviewApp.PreviewValueType) {
    let content = null;
    let contentType = "" as PreviewApp.PreviewContentType;
    switch (type) {
      case "stockInfo":
        content = {
          logo: value?.logo || "",
          name: value?.name || "N/A",
          symbol: value?.symbol || "N/A",
          price: value?.price?.replace("USD", "$") || "N/A",
          summary: value?.businessSummary || "N/A",
          volume: value?.volume || "N/A",
          value,
        };
        contentType = "stock";
        break;
      case "video":
        content = value;
        contentType = "video";
        break;
      case "jdShopping":
        content = value;
        contentType = "jdShopping";
        break;
      case "amazonShopping":
        content = value;
        contentType = "amazonShopping";
        break;
      case "weeeShopping":
        content = value;
        contentType = "weeeShopping";
        break;
      case "showFoodInterface":
        content = value;
        contentType = "showFoodInterface";
        break;
      case "imageGen":
      case "midjourney":
      case "midjourneyV2":
      case "imageSearch":
        content = value;
        contentType = "image";
        break;
      case "htmlTemplate":
        content = {
          code: [
            {
              codex_generation: value,
              notUseAnimation: true,
            },
          ],
        };
        contentType = "code";
        break;
      case "customJsFunction":
        content = value;
        contentType = "customJsFunction";
        break;
      case "customApiFunction":
        content = value;
        contentType = "customApiFunction";
        break;
      case "chatBox":
        content = value;
        contentType = "chatBox";
        break;
      case "map":
        content = value;
        contentType = "map";
        break;
      case "askBrain":
        content = value;
        contentType = "askBrain";
        break;
      case "baseVideo":
        content = value;
        contentType = "baseVideo";
        break;
      case "pdfFile":
        content = value;
        contentType = "pdfFile";
        break;
      case "audio":
        content = value;
        contentType = "audio";
        break;
      case "table":
        content = value;
        contentType = "table";
        break;
      case "uploadImage":
        content = value;
        contentType = "uploadImage";
        break;
      case "unStructured":
        content = value;
        contentType = "unStructured";
        break;
      case "chatInterface":
        content = value;
        contentType = "chatInterface";
        break;
      case "uploadFile":
        content = value;
        contentType = type;
        break;
      case "html":
        content = value;
        contentType = type;
        break;
      case "interviewBot":
        content = value;
        contentType = type;
        break;
      case "json":
        content = value;
        contentType = "textbox";
        break;
      default:
        content = value;
        contentType = "textbox";
    }
    return {
      content,
      contentType,
    };
  }

  // 以下三个属性替换原来 uiconfig 中的 previewIndexRef
  static thinkingAppPreviewIndexRef: React.MutableRefObject<number> = { current: 0 };
  static saasPreviewIndexRef: React.MutableRefObject<number> = { current: 0 };
  static saasV2PreviewIndexRef: React.MutableRefObject<number> = { current: 0 };

  /**
   * 用指定输出和节点获取 preview 预览数据
   * - from `HomeUsePlugin` getPreviewData
   * - level 用来控制优先级，如果 level 为 single, 则表示子节点优先，那么返回结果不会有 groupId
   *
   * @param type
   * @param value
   * @returns
   */
  static getPreviewData({
    output,
    previewIndex,
    node,
    previewId,
    level,
  }: {
    output: AISaasOutput.ThoughtType[];
    previewIndex: React.MutableRefObject<number>;
    node: Node<any>;
    previewId?: string;
    /**
     * 优先级，目前只支持 子节点优先
     */
    level?: "single";
  }) {
    const editorImageScrollDireaction = (store.getState().fot as any).editorImageScrollDireaction as
      | "horizontalScroll"
      | "verticallyScroll";

    // FIXME: 子节点优先，去掉 parentNode, 为了保证组节点和 组的子节点能够同时在 output 中
    const groupId = level === "single" ? "" : get(node, "parentNode", "");

    const result: AISaasOutput.ThoughtType = {
      angle: "",
      implication: "",
      modifier: "",
      reason: "",
      groupId: groupId,
      results: [],
      grid: false,
    };
    const _previewId = previewId || `preview-${previewIndex.current}`;
    /**
     * outputFontStyle： 从priview数据获取到设置字体相关数据，并赋值到canvas 数据中，避免后续canvas数据和preview数据合并时字体设置丢失
     */
    let outputFontStyle: any = {};
    if (output[0]?.results[0]?.nodeFontStyle) {
      outputFontStyle = output[0].results[0].nodeFontStyle;
    }

    const resObj: AISaasOutput.ThoughtResults = {
      previewId: _previewId,
      parentNode: groupId,
      nodeId: node.id,
      fontSize: "Normal",
      layout: "textBeautify",
      angles: [],
      content: null as any,
      dislikes: [],
      filters: [],
      likes: [],
      reactions: [],
      thought: "",
      type: "",
      nodeFontStyle: { ...outputFontStyle },
    };
    const type = node.data.displayType;
    let value = node.data.textAreaValue;

    if (type === "chatBox") {
      value = {
        textAreaValue: node?.data?.textAreaValue || [],
        chatBoxParam: node?.data?.chatBoxParam || [],
        nodeId: node?.id || "",
      };
    }

    const { content, contentType } = PreviewAppUtil.getContentByParam(type, value);
    previewIndex.current++;
    resObj.content = content;
    resObj.type = contentType;

    // FIXME: 未来版本是否应该去掉？
    if (
      editorImageScrollDireaction === "horizontalScroll" &&
      (contentType === "image" || (contentType === "customApiFunction" && content?.data?.type === "image"))
    ) {
      const imageIndex = output.findIndex(x => x.angle === "Image List");
      if (imageIndex !== -1) {
        const image = output[imageIndex];
        output.splice(imageIndex, 1, {
          ...image,
          results: image.results.concat([resObj]),
        });
        // return [...output]
        return {
          prevData: output,
          result: {} as AISaasOutput.ThoughtType,
        };
      } else {
        result.angle = "Image List";
      }
    }
    result.results.push(resObj);
    // return [...output, result]
    return {
      prevData: output,
      result,
    };
  }

  /**
   * 合并 canvas 上最新 nodes 节点数据到 指定 inputList
   * @param inputList
   * @param current
   * @returns
   */
  static mergeNodesInput(inputList: any[], current: Node<any>[]) {
    const resultList: any[] = [];
    inputList.forEach(input => {
      const target = find(current, ["id", input.id]);
      if (target) {
        // FIXME: functions 需要 在 input 中过滤掉
        resultList.push(
          PreviewAppUtil.mergeNode(
            input,
            target,
            // 当输入时 upload 时，上传内容不应该被合并，应保持不变
            ReactFlowNodeUtil.isUploadNode(input) ? PREVIEW_MERGE_UPLOAD_EXCLUDE_PATHS : undefined
          )
        );
      }
      // 不存在的节点不增加直接过滤掉
    });
    return resultList;
  }

  /**
   * 合并 canvas 上最新 nodes 节点数据到 指定 previewList
   *
   * - previewList 是 output 转换之后的数据
   *
   * @param outputList
   * @param nodes
   * @returns
   */
  static mergeNodesOutput(
    outputs: AISaasOutput.ThoughtType[],
    nodes: Node<any>[],
    saasUIData: PreviewApp.UISaasValueType
  ) {
    // 统计 outpuList 中所有 node id,group id 比较特殊有多个子节点
    // 但不能按顺序依次将原来的节点用新的节点替换，因为会存在一个问题就是，current 中已经没有指定节点了，group id需要当作一个节点看
    const processedGroups = {} as Record<string, boolean>;
    let resultOutputList = [] as AISaasOutput.ThoughtType[];
    outputs.forEach(output => {
      // 处理 组节点
      const groupId = output.groupId;
      if (ReactFlowNodeUtil.isGroupNodeId(groupId)) {
        const isProcessed = processedGroups[groupId];
        if (isProcessed) {
          return;
        }

        const outputList = PreviewAppUtil.getPreviewOutputByGroupNode({
          groupChildeNodes: ReactFlowNodeUtil.getSubNodesByGroupId(groupId, nodes),
          getResult: true,
          output: [output],
          previewIndexRef: PreviewAppUtil.saasPreviewIndexRef,
          addContentId: "",
        });
        resultOutputList = resultOutputList.concat(outputList);
        processedGroups[groupId] = true;
        return;
      }

      // 处理 单个节点
      const nodeId = get(output, ["results", "0", "nodeId"]) as string | undefined;
      if (!nodeId) {
        // 正常情况不应该到这里
        return;
      }

      const targetNode = ReactFlowNodeUtil.getNodeById(nodeId, nodes);
      // 节点已经不存在，直接跳过
      if (!targetNode) {
        return;
      }

      const outputList = PreviewAppUtil.getPreviewOutputBySingleNode({
        saasUIData: saasUIData,
        output: [output],
        node: targetNode,
        // FIXME: homeUsePlugin 使用的是 uiconfig 类型区分的indexRef，这里直接使用 saasIndexRef, 最好直接使用uuid
        previewIndexRef: PreviewAppUtil.saasPreviewIndexRef,
        addContentId: "",
      });

      resultOutputList = resultOutputList.concat(outputList);
    });
    return resultOutputList;
  }

  /**
   * 合并 canvas 上最新nodes 节点数据到 指定 app
   * - 包含 input 和 ouput
   * @param target
   * @param current
   */
  static mergeNodes(target: PreviewApp.UISaasValueType, current: Node<any>[]): PreviewApp.UISaasValueType {
    return {
      ...target,
      input: PreviewAppUtil.mergeNodesInput(target.input, current),
      output: PreviewAppUtil.mergeNodesOutput(target.output, current, target),
    };
  }

  /**
   * 合并 canvas 上最新nodes 节点数据到 一组 app list 中
   * @param appList
   * @param nodes
   */
  static mergeNodesWithAppList(
    appList: PreviewApp.UISaasValueType[],
    nodes: Node<any>[]
  ): PreviewApp.UISaasValueType[] {
    return appList.map(app => PreviewAppUtil.mergeNodes(app, nodes));
  }

  /**
   * 对 output 去重
   *
   * 去重时可优先使用后面的元素
   *
   * 1. 每个 node 只能出现一次
   * 2. gourpNode 的子node 最多可以出现两次，因为可以 group 子node和 group 同时添加
   *
   * 简单判断就是用 外层的 groupId + results[0].nodeId 作为唯一key 判断是否重复
   * - 当有多个相同 nodeId 时，如果有 groupID 则表示通过group增加，如果没有则表示单个增加
   */
  static unionOutput<T extends AISaasOutput.ThoughtType>(ouputList: T[], level: "right" | "left" = "right"): T[] {
    const getUnionKey = (a: T) => {
      const aGroupId = get(a, "groupId", "");
      const aNodeId = get(a, ["results", "0", "nodeId"]);
      const unionKey = aGroupId + aNodeId;
      return unionKey;
    };

    if (level === "left") {
      return unionBy(ouputList, getUnionKey);
    }

    const result: T[] = [];
    const unioMap: Record<string, boolean> = {};
    eachRight(ouputList, output => {
      const key = getUnionKey(output);
      if (!unioMap[key]) {
        result.unshift(output);
        unioMap[key] = true;
      }
    });
    return result;
  }

  /**
   * 用来判断 nodeid 是否已经被添加到 output 中
   *
   * - nodeid 为 group id 的情况, 组节点是否添加按照当前 output 到preview 的逻辑来看，output 中只要有 groupid 则表示已经添加, 因为组的子节点单独添加不会包含 groupID
   * - nodeid 为普通节点的情况，
   * @param nodeId
   * @param output
   */
  static hasAddedOutputByNode(nodeId: string, outputList: AISaasOutput.ThoughtType[]) {
    if (ReactFlowNodeUtil.isGroupNodeId(nodeId)) {
      // FIXME: 组的子节点单独添加不会包含 groupID， 需要完善判断逻辑
      return !!find(outputList, ["groupId", nodeId]);
    }

    // 普通节点
    for (const output of outputList) {
      for (const result of output.results) {
        // 如果该节点找到，且groupId 存在则表示该节点是通过组节点增加进 output, 否则才是子节点单独增加
        if (result.nodeId === nodeId && !output.groupId) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * 兼容首页 原来的 `addSingleNodeToPreview` 核心方法
   *
   * 用来获取当node的 previewList（因为某些当个 node 可能会产生多个 preview）
   *
   * **内部有对返回结果去重，但是该去重范围仅限于传入的 output 作为所有最终的 saasuidata.output, 也就是如果传入的 output 并非 saasuidata.output 或者只是部分 output, 那么在调用该方法的最后需要手动去重**
   *
   * @param param0
   * @returns
   */
  static getPreviewOutputBySingleNode({
    saasUIData,
    output,
    previewIndexRef,
    addContentId,
    node,
  }: {
    saasUIData: PreviewApp.UISaasValueType;
    output: AISaasOutput.ThoughtType[];
    previewIndexRef: { current: number };
    addContentId: string;
    node: Node;
  }) {
    const { prevData, result } = PreviewAppUtil.getPreviewData({
      output: output,
      previewIndex: previewIndexRef,
      node,
      // !!! getPreviewOutputBySingleNode 一定是子节点优先，不能将 groupId 在 result 中返回
      // 这样在调用 getPreviewOutputBySingleNode 时一定保证没有 groupId 的值，从源头和组区分
      level: "single",
    });

    // stud-1832 chat
    if (PreviewAppUtil.isChatAiApp(saasUIData)) {
      return [result];
    }

    let list = [] as AISaasOutput.ThoughtType[];
    let stopNextIsGroup = false;
    if (addContentId && addContentId !== WITHOUT_INPUT_ID && addContentId !== AYLWAYS_SHOW_OUTPUT) {
      prevData.forEach(item => {
        if ((item.groupId === addContentId && !stopNextIsGroup) || item.results[0].previewId === addContentId) {
          if (item.groupId === addContentId) stopNextIsGroup = true;
          list.push(result);
          list.push(item);
        } else {
          list.push(item);
        }
      });
    } else {
      list = PreviewAppUtil.unionOutput([...prevData, result]);
    }
    return list;
  }

  /**
   * 根据groupId创建output
   *
   * **只会在mobile中cot生成的flow调用，根据边上文字预判模拟输出组**
   *
   * @param groupId 组的Id
   * @returns AISaasOutput.ThoughtTyp
   */
  static createGroupOutput(groupId: string): AISaasOutput.ThoughtType {
    return {
      groupId,
      angle: "",
      implication: "",
      modifier: "",
      reason: "",
      results: [
        {
          previewId: "",
          nodeId: "",
          angles: [],
          content: [],
          dislikes: [],
          filters: [],
          fontSize: "Normal",
          layout: "textBeautify",
          likes: [],
          parentNode: groupId,
          reactions: [],
          thought: "",
          type: "text",
        },
      ],
      grid: false,
    };
  }

  /**
   * 兼容首页 原来的 `addGroupNodeToPreview` 核心方法
   *
   * 用来获取当 groupNodes 的 previewList
   *
   * **内部有对返回结果去重，但是该去重范围仅限于传入的 output 作为所有最终的 saasuidata.output, 也就是如果传入的 output 并非 saasuidata.output 或者只是部分 output, 那么在调用该方法的最后需要手动去重**
   * @param param0
   * @returns
   */
  static getPreviewOutputByGroupNode({
    groupChildeNodes,
    addContentId,
    output,
    previewIndexRef,
    getResult,
  }: {
    groupChildeNodes: Node[];
    output: AISaasOutput.ThoughtType[];
    previewIndexRef: { current: number };
    getResult: boolean;
    addContentId: string;
  }) {
    let resultList = [] as AISaasOutput.ThoughtType[];
    let tempPrevData = [] as any[];
    groupChildeNodes.forEach(node => {
      const { prevData, result } = PreviewAppUtil.getPreviewData({
        output: output,
        previewIndex: previewIndexRef,
        node,
      });
      tempPrevData = prevData;
      resultList = func.isEmpty(result) ? resultList : [...resultList, result];
    });
    // mobile-cot 生成的cot 根据边上文字预判模拟输出组 此时组为空 但是也必须加入到output里
    if (groupChildeNodes.length === 0) {
      const groupId = output[0].groupId;
      resultList.push(PreviewAppUtil.createGroupOutput(groupId));
    }
    if (getResult) {
      return resultList;
    }
    let list = [] as AISaasOutput.ThoughtType[];
    let stopNextIsGroup = false;
    if (addContentId && addContentId !== WITHOUT_INPUT_ID && addContentId !== AYLWAYS_SHOW_OUTPUT) {
      tempPrevData.forEach(item => {
        if ((item.groupId === addContentId && !stopNextIsGroup) || item.results[0].previewId === addContentId) {
          if (item.groupId === addContentId) stopNextIsGroup = true;
          list.push(...resultList);
          list.push(item);
        } else {
          list.push(item);
        }
      });
    } else {
      list = PreviewAppUtil.unionOutput([...tempPrevData, ...resultList]);
    }

    return list;
  }

  static isChatAiApp(saasUiData: PreviewApp.UISaasValueType) {
    return get(saasUiData, ["v2App", "type"]) === PreviewAppTypeMap.CHAT_AI;
  }

  static checkInputLengthAndType(input: Node[], types: string[], length = 1) {
    if (isArray(input) && input.length !== length) {
      return false;
    }
    const displayType = first(input)?.data.displayType;
    return isString(displayType) ? types.includes(displayType) : false;
  }
  static checkOutputLengthAndType(output: AISaasOutput.ThoughtType[], types: string[], length = 1) {
    if (isArray(output) && output.length !== length) {
      return false;
    }
    const displayType = get(output, ["0", "results", "0", "type"]);
    return isString(displayType) ? types.includes(displayType) : false;
  }

  /**
   * 该方法用于验证当前 saasuidata 中的 input 和 output 是否满足以下两个条件
   *
   * 1. input 和 output 分别只存在一个节点
   * 2. 其中的节点应该是同一条边上的节点
   *
   * @param args
   * @returns
   */
  static isRelationNodeWithChatAI(args: PreviewApp.GetChatAiModeArgs) {
    const { saasUIData, nodes } = args;
    const { input, output } = saasUIData;

    if (
      PreviewAppUtil.checkInputLengthAndType(input, PREVIEW_CHATAI_PUBLISH_INPUT_TYPES) &&
      PreviewAppUtil.checkOutputLengthAndType(output, PREVIEW_CHATAI_PUBLISH_OUTPUT_TYPES)
    ) {
      // 并且检查 input, output 两个节点是否为同一组,也就是 input 边连接的就是 output
      const startNodeId = get(input, ["0", "id"]);
      const endNodeId = get(output, ["0", "results", "0", "nodeId"]);

      if (startNodeId && endNodeId) {
        return PreviewAppUtil.isRelationNode({ nodes, startNodeId: [startNodeId], endNodeId });
      }
    }

    return false;
  }

  static getChatAiMode(args: PreviewApp.GetChatAiModeArgs): PreviewApp.ChatAppModeType | undefined {
    const { input, output } = args.saasUIData;

    const hasOutput = !isEmpty(output);
    const hasInput = !isEmpty(input);

    // 有输出/有输入
    // 2.1 只有输出, 只有一个 chatinterface 可发布
    if (hasOutput && !hasInput && isOnlyChatInterface(output)) {
      return "chat";
    }

    // 2.2 只有输入
    if (
      hasInput &&
      !hasOutput &&
      PreviewAppUtil.checkInputLengthAndType(
        input,
        PREVIEW_CHATAI_PUBLISH_INPUT_TYPES.concat(PREVIEW_CHATAI_PUBLISH_OUTPUT_TYPES)
      )
    ) {
      if (PreviewAppUtil.checkInputLengthAndType(input, PREVIEW_CHATAI_PUBLISH_OUTPUT_TYPES)) {
        return "chat";
      }

      if (PreviewAppUtil.checkInputLengthAndType(input, [StoryNodeDisplayType.TEXT])) {
        return "text";
      }
      return "upload";
    }

    // 2.3 有输出 && 有输入, 只有一个 text/upload 作为输入, chatinterface 作为输出 可发布
    if (hasInput && hasOutput) {
      if (PreviewAppUtil.checkInputLengthAndType(input, [StoryNodeDisplayType.TEXT])) {
        return "text";
      }
      if (PreviewAppUtil.checkInputLengthAndType(input, PREVIEW_CHATAI_PUBLISH_OUTPUT_TYPES)) {
        return "chat";
      }
      return "upload";
    }

    return;
  }

  /**
   * 1. 没有输入和输出 禁止发布
   * 2. 有输出/有输入
   *  2.1 只有输出, 只有一个 chatinterface 可发布
   *  2.2 只有输入, 只有一个 chatinterface 可发布
   *  2.3 有输出 && 有输入, 只有一个 text/upload 作为输入, chatinterface 作为输出 可发布
   * 3. 其他情况 禁止发布
   *
   * @param saasUIData
   * @returns
   */
  static isDisabledPublishByChatAi(args: PreviewApp.DisabledPublishArgs) {
    const { saasUIData } = args;
    const { input, output } = saasUIData;

    const hasOutput = !isEmpty(output);
    const hasInput = !isEmpty(input);

    // 输入输出都没有 禁用发布
    if (!hasOutput && !hasInput) {
      return true;
    }

    let chatMode = PreviewAppUtil.getChatAiMode(args);
    //只有input时，只能是chat可发布，其他的不可以发布
    if (hasInput && !hasOutput && chatMode !== "chat") {
      return true;
    }
    return !chatMode;
  }
  /**
   *
   * aiapp,static 类型: 使用上一版本逻辑
   *
   * chat ai 类型: `isDisabledPublishByChatAi`
   *
   * @param args
   */
  static isDisabledPublish(args: PreviewApp.DisabledPublishArgs) {
    const { saasUIData, isStaticMode, thoughts, selectedUI } = args;
    // FIXME: 不应该使用 thoughts
    const isEmptyOutput = isEmpty(thoughts);

    // chat ai disabled
    if (PreviewAppUtil.isChatAiApp(saasUIData)) {
      return PreviewAppUtil.isDisabledPublishByChatAi(args);
    }

    // 上一次的逻辑
    if (isOnlyChatInterface(saasUIData.output)) {
      return false;
    }

    if (isStaticMode && isEmptyOutput === false) {
      return false;
    }

    const hasInput = !func.isEmpty(saasUIData.input);
    const hasOutput = !func.isEmpty(saasUIData.output);
    return !(hasInput && hasOutput);
  }

  /**
   * 开始节点和结束节点是否有关联(在同一条边上)
   * @param args
   * @returns
   */
  static isRelationNode(args: { startNodeId: string[]; endNodeId: string; nodes: Node[] }): boolean {
    const relatedEdgeNode = args.nodes.filter(node =>
      node.data?.flows?.some((source: any) => args.startNodeId.includes(source.sourceNodeId))
    );
    //如果找不到start node连的边，没有关联
    if (func.isEmpty(relatedEdgeNode)) return false;
    const nextNodesId = relatedEdgeNode.map(node => node?.data?.targetNodeId);
    if (func.isEmpty(nextNodesId)) return false;
    //target中能找到endNodeId，有关联
    if (nextNodesId.includes(args.endNodeId)) return true;

    return PreviewAppUtil.isRelationNode({ ...args, startNodeId: nextNodesId });
  }
  static isChatNode(node: Node) {
    return node?.data?.displayType === "chatInterface";
  }
  //判定当前节点之后是否有chat node
  static hasRelatedChatNode(args: { startNodeId: string[]; nodes: Node[] }): boolean {
    const relatedEdgeNode = args.nodes.filter(node =>
      node.data?.flows?.some((source: any) => args.startNodeId.includes(source.sourceNodeId))
    );
    //如果找不到start node连的边，没有关联
    if (func.isEmpty(relatedEdgeNode)) return false;
    const nextNodesId: string[] = relatedEdgeNode.map(node => node?.data?.targetNodeId);
    if (func.isEmpty(nextNodesId)) return false;
    const nextNodes = args.nodes.filter(node => nextNodesId.includes(node.id));
    const nextNodeHasChatNode = nextNodes.some(node => PreviewAppUtil.isChatNode(node));
    //target中能找到chat node，有关联
    if (nextNodeHasChatNode) return true;

    return PreviewAppUtil.hasRelatedChatNode({ ...args, startNodeId: nextNodesId });
  }
  //判定是否需要显示禁用的tool bar提示
  static needDisableSendToAppBtn(args: {
    isChat: boolean;
    nodeId: string;
    isStartNode: boolean;
    nodes: Node[];
  }): boolean {
    if (!args.isChat) return false;
    const currentNode = args.nodes.find(node => node.id === args.nodeId);
    if (!currentNode) return true;

    if (args.isStartNode) {
      return (
        !PreviewAppUtil.isChatNode(currentNode) &&
        !PreviewAppUtil.hasRelatedChatNode({
          startNodeId: [args.nodeId],
          nodes: args.nodes,
        })
      );
    }
    return !PreviewAppUtil.isChatNode(currentNode);
  }

  /**
   * from 'homeUsePlugin' HandleInputField `UIConfigRef.current[typeSelected].setUIDataFunc(prevData` 部分
   *
   * 用来获取handleInputField 处理后的数据
   *
   * @param args
   * @returns
   */
  static genHandleInputField(args: {
    saasUIData: PreviewApp.UISaasValueType;
    currentNode: Node;
    addContentIdRef: React.MutableRefObject<string>;
    selectedTemplate: PreviewApp.ProjectType;
  }) {
    const oldInputNode = args.saasUIData.input || [];
    const prevData = args.saasUIData;
    const { selectedTemplate, currentNode, addContentIdRef } = args;

    let obj = {};
    if (isEmpty(oldInputNode)) {
      // stud-1221 change title&subTitle
      obj = {
        title: prevData.fromCotTitle || selectedTemplate?.name || WEB_APP_DEFAULT_TITLE,
        subTitle: prevData.fromCotSubtitle || WEB_APP_DEFAULT_SUBTITLE,
      };
    }
    /**
     * 如果uploadTypes中的类型作为input node
     * 修改类型为uploadFile
     */
    const isUploadTypesNode = uploadTypes.includes(currentNode?.data?.displayType);
    const newCurrentNode = isUploadTypesNode
      ? { ...currentNode, data: { ...currentNode.data, displayType: "uploadFile", textAreaValue: "" } }
      : { ...currentNode };
    let newInputNode = [] as any[];
    if (addContentIdRef.current && addContentIdRef.current !== WITHOUT_INPUT_ID) {
      oldInputNode.forEach(item => {
        if (item.id === addContentIdRef.current) {
          newInputNode.push(newCurrentNode);
          newInputNode.push(item);
        } else {
          newInputNode.push(item);
        }
      });
    } else {
      newInputNode = [...oldInputNode, newCurrentNode];
    }
    return {
      ...prevData,
      ...obj,
      input: [...newInputNode],
    };
  }

  static handleChatAIAppField(args: {
    saasUIData: PreviewApp.UISaasValueType;
    currentNode: Node;
    nodes: Node[];
    addContentIdRef: React.MutableRefObject<string>;
    selectedTemplate: PreviewApp.ProjectType;
    setSaasUIData: React.Dispatch<React.SetStateAction<PreviewApp.UISaasValueType>>;
    setInputId: (param: any) => void;
  }) {
    const { currentNode, addContentIdRef, selectedTemplate } = args;
    const inputId = currentNode.id;

    // 查找上一次 saasuidata 中的 input 和 output 的关系
    // 1. 检查 currentNode 在input 和 output 中的关系
    //
    // 1.1 如果是单边边
    //  输入时: 直接替换当前input
    //  输出时: 直接替换对应输出
    // 1.2 多边: 替换当前input; 添加output时，替换当前output
    //
    // 2. 如果 input 或 output 中和 currentNode 没有关联,则直接移除这些节点
    // FIXME: 内部判断使用output 第一个节点，有可能会因为数据不正确，第一个节点数据错误
    // stud-1832 chat ai output
    // chat 只有单输入输出
    function genChaiAiInput(prev: PreviewApp.UISaasValueType) {
      return [currentNode];
    }

    args.setSaasUIData(prev => {
      return {
        ...prev,
        input: genChaiAiInput(prev),
      };
    });
    args.setInputId([inputId]);

    // PreviewAppUtil.handleChatPreviewOutputByNewInput({
    //   inputId,
    //   output: args.saasUIData.output,
    //   nodes: args.nodes,
    //   setSaasUIData: args.setSaasUIData,
    // });
  }

  static clearChatOutput(setSaasUIData: React.Dispatch<React.SetStateAction<PreviewApp.UISaasValueType>>) {
    setSaasUIData((prev: any) => {
      return {
        ...prev,
        output: [],
      };
    });
  }
  //chat添加input，处理output
  static handleChatPreviewOutputByNewInput(args: {
    inputId: string;
    output: AISaasOutput.ThoughtType[];
    nodes: Node[];
    setSaasUIData: React.Dispatch<React.SetStateAction<PreviewApp.UISaasValueType>>;
  }): void {
    if (func.isEmpty(args.output)) return;
    const outputNodeId = args.output[0]?.results?.[0]?.nodeId;
    if (func.isEmpty(outputNodeId)) return;
    const isRelated = PreviewAppUtil.isRelationNode({
      startNodeId: [args.inputId],
      endNodeId: outputNodeId,
      nodes: args.nodes,
    });
    //如果和当前input无关，删除当前output
    if (!isRelated) {
      PreviewAppUtil.clearChatOutput(args.setSaasUIData);
    }
  }

  //chat添加output，处理input
  static handleChatPreviewInputByNewOutput(args: {
    outputId: string;
    input: Node[];
    nodes: Node[];
    setSaasUIData: React.Dispatch<React.SetStateAction<PreviewApp.UISaasValueType>>;
    setInputId: (param: any) => void;
  }): void {
    if (func.isEmpty(args.input)) return;
    const inputNodeId = args.input[0]?.id;
    if (func.isEmpty(inputNodeId)) return;
    const isRelated = PreviewAppUtil.isRelationNode({
      startNodeId: [inputNodeId],
      endNodeId: args.outputId,
      nodes: args.nodes,
    });
    //如果和当前output无关，删除当前input
    if (!isRelated) {
      args.setSaasUIData((prev: any) => {
        return {
          ...prev,
          input: [],
        };
      });
      args.setInputId([]);
    }
  }

  static getChatOutputEdgeId(output: AISaasOutput.ThoughtType[], nodes: Node[]): string | undefined {
    const chatNodeId = output?.[0]?.results?.[0]?.nodeId;
    if (!chatNodeId) return undefined;

    const edgeNode = nodes.find(node => node.data?.targetNodeId === chatNodeId);
    if (!edgeNode) return undefined;
    return edgeNode?.id;
  }
  //根据saasUIData和previewAppList，拿到chat ai app相关的id
  static handleChatOutputEdgeId(args: {
    saasUIData: PreviewApp.UISaasValueType;
    previewAppList: PreviewApp.UISaasValueType[];
    nodes: Node[];
  }): Array<string | undefined> {
    let arr: Array<string | undefined> = [];
    //当前saasUIData是chat ai app,如果有chat的output，则添加chat的边id
    const previewDetailIsChat = PreviewAppUtil.isChatAiApp(args.saasUIData);
    if (previewDetailIsChat) {
      const edgeId = PreviewAppUtil.getChatOutputEdgeId(args.saasUIData.output, args.nodes);
      if (edgeId) arr.push(edgeId);
    }
    //preview app list挨个循环判定，如果是chat ai app，有chat的output，则添加chat的边id
    args.previewAppList.forEach(previewApp => {
      const previewAppIsChat = PreviewAppUtil.isChatAiApp(previewApp);
      if (!previewAppIsChat) return;
      const edgeId = PreviewAppUtil.getChatOutputEdgeId(previewApp.output, args.nodes);
      if (edgeId && !arr.includes(edgeId)) arr.push(edgeId);
    });
    const chatAIStore = getIt(ChatAIStore);
    chatAIStore.setChatNodePreEdgeId(arr);
    return arr;
  }
  //chat边变化后，删除相关的output
  static deleteChatAiOutput(args: {
    edgeId: string;
    saasUIData: PreviewApp.UISaasValueType;
    previewAppList: PreviewApp.UISaasValueType[];
    nodes: Node[];
    setSaasUIData: React.Dispatch<React.SetStateAction<PreviewApp.UISaasValueType>>;
    replacePreviewAppList: (newPreviewAppList: PreviewApp.UISaasValueType[]) => void;
  }) {
    //saasUIData
    const saasEdgeId = PreviewAppUtil.handleChatOutputEdgeId({
      saasUIData: args.saasUIData,
      previewAppList: [],
      nodes: args.nodes,
    });
    if (!func.isEmpty(saasEdgeId) && saasEdgeId.includes(args.edgeId)) {
      PreviewAppUtil.clearChatOutput(args.setSaasUIData);
    }
    const newPreviewAppList = args.previewAppList.map((app: PreviewApp.UISaasValueType) => {
      const currentOutputPreEdgeId = PreviewAppUtil.handleChatOutputEdgeId({
        saasUIData: app,
        previewAppList: [],
        nodes: args.nodes,
      });
      if (!func.isEmpty(currentOutputPreEdgeId) && currentOutputPreEdgeId.includes(args.edgeId)) {
        return {
          ...app,
          output: [],
        };
      }
      return app;
    });
    //previewAppList
    args.replacePreviewAppList(newPreviewAppList);
  }
  static disabledPublishToOutput(args: { nodeId: string; saasUIData: PreviewApp.UISaasValueType; nodes: Node[] }) {
    const homePluginStore = getIt(HomePluginStore);
    const hasAddedToPreview = args.saasUIData.output
      .filter(x => !x?.groupId)
      .map(x => (x?.results || []).map(r => r.nodeId))
      ?.flat(Infinity)
      ?.includes(args.nodeId);
    const isChat = PreviewAppUtil.isChatAiApp(args.saasUIData);

    if (!isChat) {
      homePluginStore.setDisablePublishAsContent(hasAddedToPreview);
      return;
    }

    const currentNode = args.nodes.find(node => node.id === args.nodeId);
    const isChatNode = currentNode && PreviewAppUtil.isChatNode(currentNode);
    homePluginStore.setDisablePublishAsContent(hasAddedToPreview || !isChatNode);
    return hasAddedToPreview || !isChatNode;
  }

  /**
   * 判断 output 中的 result 结构是否显示
   * @param result
   * @returns
   */
  static isVisibleOutputResult(result: AISaasOutput.ThoughtResults): boolean {
    // 如果有报错，那么也需要保留并显示出来。
    if (!func.isEmpty(result?.errorText)) return !!result;
    switch (result.type) {
      case "stock":
        return !func.isEmpty(result?.content?.symbol) && !func.isEmpty(result?.content?.value);
      case "code":
        return !func.isEmpty(result?.content?.code?.[0]?.codex_generation);
      case "chatBox":
        return !!result;
      default:
        return !func.isEmpty(result.content);
    }
  }

  /**
   * 获取扁平化后的 output 列表中的所有 results
   * @param outputList
   * @returns
   */
  static getFlatResults(outputList: AISaasOutput.ThoughtType[]): AISaasOutput.ThoughtResults[] {
    const results: AISaasOutput.ThoughtResults[] = [];
    for (const output of outputList) {
      for (const result of output.results) {
        results.push(result);
      }
    }
    return results;
  }

  /**
   * 合并上一次的 output 和 新 outputList
   *
   * !!! 改方法用于是否在替换
   *
   * @param prevOutput
   * @param outputList
   * @returns
   */
  static mergeOutputList(
    prevOutput: AISaasOutput.ThoughtType[],
    outputList: AISaasOutput.ThoughtType[]
  ): AISaasOutput.ThoughtType[] {
    const prevResutsList = PreviewAppUtil.getFlatResults(prevOutput);
    return outputList.map(output => {
      const newResults = output.results.map(result => {
        if (!PreviewAppUtil.isVisibleOutputResult(result)) {
          const prevResult = prevResutsList.find(item => {
            return item.nodeId === result.nodeId && item.parentNode === result.parentNode;
          });
          if (prevResult) {
            return {
              ...result,
              content: prevResult.content,
            };
          }
        }
        return result;
      });
      return {
        ...output,
        results: newResults,
      };
    });
  }

  static needDisableBluePrintCollapse(props: { nodeId: string; nodes: Node[]; edges: Edge[] }): boolean {
    const allNodes = [props.nodeId];
    const parentIds = [props.nodeId];

    while (parentIds.length) {
      const current = parentIds.pop();
      const childs = filter(props.nodes, x => x.data.parentNodeId === current);
      const childIds = map(childs, x => x.id);
      allNodes.push(...childIds);
      parentIds.push(...childIds);
    }

    return some(props.edges, edge => allNodes.includes(edge.source));
  }

  /**
   * 获取默认的 preview app 中的 subtitle
   *
   * bsf-6211 的时候 subtitle 默认没有了
   *
   * @param previewApp
   * @returns
   */
  static getPreviewAppDefualtSubtitle(previewApp: PreviewApp.UISaasValueType): string {
    if (previewApp.subTitle) {
      return previewApp.subTitle;
    }

    if (PreviewAppUtil.isChatAiApp(previewApp)) {
      return "";
    }
    return WEB_APP_DEFAULT_SUBTITLE;
  }
}
